/**
 * Shared display-name resolution for Share360 leads.
 * Single source of truth — mirrors the pipeline card logic (the reference implementation).
 *
 * Share360 Business  → primary = Business Name,  secondary = contact person name
 * Share360 Nonprofit → primary = Org Name,        secondary = contact person name
 * All other leads    → primary = contact person name (unchanged)
 */

export interface S360NameInput {
  verticalName?: string | null;
  /** GHL submission_type field — "business" | "nonprofit". Used as fallback
   *  when verticalName is the generic "Share360" (not yet sub-typed). */
  sh360Role?: string | null;
  sh360BusinessName?: string | null;
  sh360OrgName?: string | null;
  businessName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  repCode?: string | null;
}

/**
 * Strip the " - {repCode}" suffix that GHL workflows append to org/business names.
 *
 * Two-pass strategy:
 *  1. Exact match when repCode is known (fastest path).
 *  2. Regex fallback for leads whose repCode column is null in the DB (not yet synced).
 *     Rep codes always start with an uppercase letter, contain at least one digit,
 *     and consist only of uppercase letters, digits, and dashes (e.g. "BC-360", "S360-001").
 *     Requiring a digit prevents false positives on suffixes like "LLC", "USA", "Inc".
 */
export function stripRepCode(s: string | null | undefined, repCode: string | null | undefined): string | null {
  if (!s) return null;
  // Pass 1: exact known-repCode match
  if (repCode && s.endsWith(` - ${repCode}`)) return s.slice(0, -(` - ${repCode}`).length);
  // Pass 2: pattern-based fallback (handles unsynced leads where repCode is null in DB)
  const m = s.match(/^(.*?)\s+-\s+([A-Z][A-Z0-9-]{2,14})$/);
  if (m && /\d/.test(m[2])) return m[1];
  return s;
}

export function resolveS360Name(lead: S360NameInput): { primary: string; secondary: string | null } {
  // Determine sub-vertical using all available signals, in priority order:
  //  1. Explicit sub-vertical name ("Share360 Business" / "Share360 Nonprofit")
  //  2. sh360Role custom field ("business" / "nonprofit")
  //  3. Presence of sh360OrgName → Nonprofit (only populated on NP form submissions)
  //  4. Presence of sh360BusinessName without sh360OrgName → Business
  // This handles leads whose verticalName is still generic "Share360" (never reclassified)
  // and whose sh360Role is null — previously those fell through to the contact-name branch.
  const isGenericShare360 = (lead.verticalName ?? "").startsWith("Share360");
  const isBiz = lead.verticalName === "Share360 Business" || lead.sh360Role === "business"
    || (isGenericShare360 && !!lead.sh360BusinessName && !lead.sh360OrgName);
  const isNP  = lead.verticalName === "Share360 Nonprofit" || lead.sh360Role === "nonprofit"
    || (isGenericShare360 && !!lead.sh360OrgName);
  const strip = (s: string | null | undefined) => stripRepCode(s, lead.repCode);
  const rawContactFull = [lead.firstName, lead.lastName].filter(Boolean).join(" ") || null;
  // Strip rep-code suffix from the contact name too — GHL workflows sometimes append it to
  // first/last name fields, matching the same behaviour as Admin & Agent pipeline cards.
  const contactFull = strip(rawContactFull);

  if (!isBiz && !isNP) {
    return {
      primary: contactFull || strip(lead.businessName) || lead.email?.split("@")[0] || "Unknown",
      secondary: null,
    };
  }

  // Share360: org/business name is primary
  const rawOrg = isBiz
    ? (lead.sh360BusinessName || lead.businessName)
    : (lead.sh360OrgName     || lead.businessName);
  const cleanedOrg = strip(rawOrg);
  const primary = cleanedOrg || contactFull || lead.email?.split("@")[0] || "Unknown";

  // Secondary line differs by sub-vertical
  const secondary = isBiz
    ? (contactFull && contactFull !== primary ? contactFull : lead.email ?? null)
    // Nonprofit: secondary = "Business Contact Name" field (sh360BusinessName), then contact person
    : (strip(lead.sh360BusinessName) || (contactFull && contactFull !== primary ? contactFull : null) || lead.email || null);

  return { primary, secondary };
}
